import { Link, useParams } from "react-router-dom";
import {
  ArrowUpRight, ArrowLeft, Check, Phone, ChevronRight, Leaf, ShieldCheck, MapPin,
} from "lucide-react";
import { CITIES, CITY_ORDER } from "@/data/cities";
import { PANEL_CITIES, PANEL_CITY_ORDER } from "@/data/panelUpgradeCities";
import { EMERGENCY_CITIES, EMERGENCY_CITY_ORDER } from "@/data/emergencyCities";
import { SECURITY_CITIES, SECURITY_CITY_ORDER } from "@/data/securityCities";
import { GENERAL_CITIES, GENERAL_CITY_ORDER } from "@/data/generalCities";
import { COMMERCIAL_CITIES, COMMERCIAL_CITY_ORDER } from "@/data/commercialCities";
import { LIGHTING_CITIES, LIGHTING_CITY_ORDER } from "@/data/lightingCities";
import { BUSINESS, IMAGES } from "@/data/content";
import useSEO from "@/hooks/useSEO";
import { faqSchema, serviceSchema, breadcrumbSchema } from "@/lib/schema";

const SERVICE_CONFIG = {
  "ev-charger-installation": {
    label: "EV Charger Installation",
    cities: CITIES,
    order: CITY_ORDER,
    guideHref: "/guides/bc-ev-charger-rebates-2026",
    guideLabel: "Read the full 2026 BC Rebates Guide",
    image: IMAGES.ev,
    kickerPrefix: "EV Charger",
  },
  "panel-upgrades": {
    label: "Electrical Panel Upgrade",
    cities: PANEL_CITIES,
    order: PANEL_CITY_ORDER,
    guideHref: "/guides/electrical-panel-upgrade-cost-bc-2026",
    guideLabel: "Read the 2026 BC Panel Upgrade Cost Guide",
    image: IMAGES.hero,
    kickerPrefix: "Panel Upgrade",
  },
  emergency: {
    label: "Emergency Electrician",
    cities: EMERGENCY_CITIES,
    order: EMERGENCY_CITY_ORDER,
    guideHref: "/services/emergency",
    guideLabel: "Back to Emergency Electrician service",
    image: IMAGES.hero,
    kickerPrefix: "Emergency",
  },
  security: {
    label: "Security & Home Automation",
    cities: SECURITY_CITIES,
    order: SECURITY_CITY_ORDER,
    guideHref: "/services/security",
    guideLabel: "Back to Security & Home Automation",
    image: IMAGES.hero,
    kickerPrefix: "Security & Automation",
  },
  "general-electrical": {
    label: "General Electrical",
    cities: GENERAL_CITIES,
    order: GENERAL_CITY_ORDER,
    guideHref: "/services/general-electrical",
    guideLabel: "Back to General Electrical service",
    image: IMAGES.about,
    kickerPrefix: "Electrician",
  },
  "commercial-wiring": {
    label: "Commercial Wiring",
    cities: COMMERCIAL_CITIES,
    order: COMMERCIAL_CITY_ORDER,
    guideHref: "/services/commercial-wiring",
    guideLabel: "Back to Commercial Wiring service",
    image: IMAGES.commercial,
    kickerPrefix: "Commercial",
  },
  lighting: {
    label: "Lighting Design & Install",
    cities: LIGHTING_CITIES,
    order: LIGHTING_CITY_ORDER,
    guideHref: "/services/lighting",
    guideLabel: "Back to Lighting Design & Install",
    image: IMAGES.skyline,
    kickerPrefix: "Lighting",
  },
};

// Renders bullet OR heading-body item.
function ItemBlock({ item, tone = "dark" }) {
  const isObj = typeof item === "object" && item !== null;
  const light = tone === "light";
  return (
    <li
      className={`flex items-start gap-3 border p-4 ${
        light ? "bg-white border-[#E4E4E7] text-[#0A0A0A]" : "bg-[#141414] border-[#262626] text-white"
      }`}
    >
      <span className="inline-flex w-6 h-6 bg-[#E60000] items-center justify-center shrink-0 mt-0.5">
        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
      </span>
      <div className="text-sm md:text-base leading-relaxed">
        {isObj && item.h && (
          <div className={`font-display font-bold text-base md:text-lg mb-1 ${light ? "text-black" : "text-white"}`}>
            {item.h}
          </div>
        )}
        <span className={light ? "text-black/75" : "text-white/80"}>{isObj ? item.p : item}</span>
      </div>
    </li>
  );
}

export default function CityServicePage({ serviceSlug = "ev-charger-installation" }) {
  const { city: slug } = useParams();
  const svc = SERVICE_CONFIG[serviceSlug];
  const c = svc.cities[slug];

  const jsonld = c
    ? [
        serviceSchema({
          name: `${svc.label} in ${c.city}`,
          description: c.metaDesc || (c.intro && c.intro[0]) || "",
          url: `/services/${serviceSlug}/${slug}`,
          image: svc.image,
          serviceType: svc.label,
          areaServed: [c.city],
          offersUrl: `/services/${serviceSlug}/${slug}`,
        }),
        breadcrumbSchema([
          { name: "Home", href: "/" },
          { name: svc.label, href: `/services/${serviceSlug}` },
          { name: c.city, href: `/services/${serviceSlug}/${slug}` },
        ]),
        faqSchema(c.faqs),
      ].filter(Boolean)
    : [];

  useSEO({
    title: c?.metaTitle || `${svc.label} | ElectroMax Systems`,
    description: c?.metaDesc || `${svc.label} across the Lower Mainland.`,
    image: svc.image,
    type: "service",
    jsonld,
  });

  if (!c) {
    return (
      <section className="pt-40 pb-32 min-h-[70vh] bg-[#0A0A0A]">
        <div className="mx-auto max-w-[900px] px-6 text-center">
          <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-3">City not found</div>
          <h1 className="font-display text-5xl font-black tracking-tight">This location&rsquo;s not on our board.</h1>
          <Link to={`/services/${serviceSlug}`} className="inline-flex mt-8 items-center gap-2 bg-[#E60000] text-white font-bold px-6 py-3">
            View {svc.label} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    );
  }

  const otherCities = svc.order.filter((s) => s !== slug).map((s) => svc.cities[s]);
  const dataHeadings = serviceSlug === "emergency" || serviceSlug === "security" || serviceSlug === "general-electrical" || serviceSlug === "commercial-wiring" || serviceSlug === "lighting";

  return (
    <div data-testid="city-service-page">
      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden grain">
        <div className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] bg-[#E60000]/10 blur-[140px] rounded-full" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/#services" className="hover:text-white">Services</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/services/${serviceSlug}`} className="hover:text-white">{svc.label}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{c.city}</span>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7 em-rise">
              <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4 inline-flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" strokeWidth={2.5} /> Serving {c.city}, {c.region || "BC"}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-[72px] leading-[0.95] tracking-tighter font-black">
                {c.h1}
              </h1>
              <div className="mt-6 space-y-4 text-base md:text-lg text-white/75 leading-relaxed max-w-2xl">
                {c.intro.map((p, i) => <p key={i}>{p}</p>)}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/#contact"
                  data-testid="city-quote-cta"
                  className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold tracking-wide px-7 py-4 text-sm transition-colors"
                >
                  Call us! <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                </Link>
                <a
                  href={BUSINESS.phoneHref}
                  className="inline-flex items-center gap-2 bg-transparent text-white border border-[#262626] hover:border-white transition-colors font-bold tracking-wide px-7 py-4 text-sm"
                >
                  <Phone className="w-4 h-4" strokeWidth={2.5} />
                  {BUSINESS.phone}
                </a>
              </div>
            </div>
            <div className="lg:col-span-5 em-rise" style={{ animationDelay: "120ms" }}>
              <div className="relative border border-[#262626]">
                <img src={svc.image} alt={`${svc.label} by licensed electricians in ${c.city}, BC`} loading="eager" fetchPriority="high" className="w-full h-[380px] md:h-[500px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/50 to-transparent" />
                <div className="absolute -top-3 -left-3 bg-[#E60000] text-white text-[10px] tracking-[0.24em] uppercase font-bold px-3 py-2">
                  BC Licensed · Insured
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Included */}
      <section className="py-24 lg:py-32 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
            <div className="md:col-span-7">
              <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-4">— {c.included.heading}</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
                {dataHeadings ? (
                  <>{c.included.heading}<span className="text-black/40">.</span></>
                ) : (
                  <>
                    Full-scope install,
                    <br />
                    <span className="text-black/40">no shortcuts.</span>
                  </>
                )}
              </h2>
            </div>
            {c.included.lead && (
              <p className="md:col-span-5 text-black/70 leading-relaxed">{c.included.lead}</p>
            )}
          </div>
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.included.items.map((it, i) => (
              <ItemBlock key={i} item={it} tone="light" />
            ))}
          </ul>
          {c.included.outro && (
            <p className="mt-10 text-black/70 leading-relaxed max-w-4xl">{c.included.outro}</p>
          )}
        </div>
      </section>

      {/* Local */}
      <section className="py-24 lg:py-32 border-t border-[#262626] bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">— {c.local.heading}</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
              {dataHeadings ? (
                <>{c.local.heading}<span className="text-[#E60000]">.</span></>
              ) : (
                <>
                  What&rsquo;s different <br />
                  <span className="text-[#E60000]">in {c.city}.</span>
                </>
              )}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-base md:text-lg text-white/75 leading-relaxed">
            {c.local.body.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* Rebates */}
      <section className="py-24 lg:py-32 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
            <div className="md:col-span-7">
              <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4 inline-flex items-center gap-2">
                <Leaf className="w-3.5 h-3.5" strokeWidth={2.5} /> {c.rebates.heading}
              </div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
                {dataHeadings ? (
                  <>{c.rebates.heading}<span className="text-black/40">.</span></>
                ) : (
                  <>
                    Rebates you can
                    <br />
                    <span className="text-black/40">actually claim.</span>
                  </>
                )}
              </h2>
            </div>
            {c.rebates.lead && (
              <p className="md:col-span-5 text-black/70 leading-relaxed">{c.rebates.lead}</p>
            )}
          </div>
          <ul className="grid md:grid-cols-3 gap-4">
            {c.rebates.items.map((it, i) => (
              <ItemBlock key={i} item={it} tone="light" />
            ))}
          </ul>
          {c.rebates.outro && (
            <p className="mt-8 text-black/70 leading-relaxed max-w-4xl">{c.rebates.outro}</p>
          )}
          {svc.guideHref && !dataHeadings && (
          <div className="mt-8">
            <Link
              to={svc.guideHref}
              data-testid="rebates-guide-link"
              className="inline-flex items-center gap-2 border border-black/20 hover:border-black text-black font-bold px-5 py-3 text-sm transition-colors"
            >
              {svc.guideLabel} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          )}
        </div>
      </section>

      {/* Why us */}
      <section className="py-24 lg:py-32 border-t border-[#262626]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">— {c.whyUs.heading}</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95] max-w-3xl">
            {dataHeadings ? (
              <>{c.whyUs.heading}<span className="text-white/40">.</span></>
            ) : (
              <>
                Why {c.city} homeowners
                <br />
                <span className="text-white/40">call us instead.</span>
              </>
            )}
          </h2>
          {c.whyUs.lead && (
            <p className="mt-6 text-white/75 max-w-3xl text-base md:text-lg leading-relaxed">{c.whyUs.lead}</p>
          )}
          {c.whyUs.bullets && (
            <ul className="mt-10 grid md:grid-cols-2 gap-4">
              {c.whyUs.bullets.map((it, i) => <ItemBlock key={i} item={it} />)}
            </ul>
          )}
          {c.whyUs.body && (
            <p className="mt-10 text-white/75 max-w-4xl text-base md:text-lg leading-relaxed">{c.whyUs.body}</p>
          )}
          {c.whyUs.outro && (
            <p className="mt-6 text-white/70 max-w-4xl leading-relaxed">{c.whyUs.outro}</p>
          )}
        </div>
      </section>

      {/* CTA banner */}
      <section className="relative border-y border-[#E60000]/40 bg-[#E60000] overflow-hidden">
        <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none">
          <div className="w-full h-full" style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(0,0,0,0.35) 0 2px, transparent 2px 14px)" }} />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-14 md:py-20 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="text-xs tracking-[0.24em] uppercase text-black/70 font-bold mb-4">
              {c.cta.heading}
            </div>
            <h3 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tighter font-black leading-[0.95]">
              {c.cta.body}
            </h3>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <a
              href={BUSINESS.phoneHref}
              className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white hover:bg-black transition-colors font-bold px-6 py-5 group"
            >
              <span className="relative inline-flex w-10 h-10 bg-[#E60000] items-center justify-center">
                <Phone className="w-4 h-4" strokeWidth={2.5} />
                <span className="absolute inset-0 border border-white/40 animate-ping" />
              </span>
              <span className="flex flex-col text-left">
                <span className="text-[10px] tracking-[0.24em] uppercase text-white/50">Call now</span>
                <span className="font-display text-xl md:text-2xl tracking-tight">{BUSINESS.phone}</span>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10 lg:px-16">
          <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-4">— Frequently Asked</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
            {c.city} {serviceSlug === "emergency" ? "emergency" : serviceSlug === "security" ? "security" : serviceSlug === "panel-upgrades" ? "panel upgrade" : serviceSlug === "general-electrical" ? "electrical" : serviceSlug === "commercial-wiring" ? "commercial" : serviceSlug === "lighting" ? "lighting" : "EV charger"}
            <br />
            <span className="text-black/40">questions.</span>
          </h2>
          <div className="mt-12 divide-y divide-[#E4E4E7] border-y border-[#E4E4E7]">
            {c.faqs.map((f, i) => (
              <details key={i} data-testid={`faq-item-${i}`} className="group py-6 [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                  <span className="font-display text-lg md:text-xl font-bold text-black">{f.q}</span>
                  <span className="mt-1 shrink-0 inline-flex w-8 h-8 border border-[#E4E4E7] items-center justify-center group-open:bg-[#E60000] group-open:border-[#E60000] group-open:text-white transition-colors">
                    <span className="block relative w-3 h-3">
                      <span className="absolute inset-x-0 top-1/2 h-[2px] bg-current" />
                      <span className="absolute inset-y-0 left-1/2 w-[2px] bg-current group-open:hidden" />
                    </span>
                  </span>
                </summary>
                <p className="mt-4 text-sm md:text-base text-black/70 leading-relaxed max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Other cities we serve */}
      <section className="py-20 border-t border-[#262626]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
            <div>
              <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-3">— Serving the Lower Mainland</div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tighter font-bold">Also installing in</h2>
            </div>
            <Link to={`/services/${serviceSlug}`} className="text-sm font-bold text-white/70 hover:text-white inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Main {svc.label} service
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {otherCities.map((o) => (
              <Link
                key={o.slug}
                to={`/services/${serviceSlug}/${o.slug}`}
                data-testid={`city-link-${o.slug}`}
                className="group border border-[#262626] hover:border-[#E60000] p-4 flex flex-col gap-1 transition-colors"
              >
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#E60000] font-bold">{svc.kickerPrefix}</span>
                <span className="font-display text-lg font-bold group-hover:text-white transition-colors">{o.city}</span>
                <ShieldCheck className="w-3.5 h-3.5 text-white/40 mt-1" strokeWidth={2} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
